import type { CurrencyCode } from '@/core/types/tenant.types';

export const CURRENCY_MAP: Record<CurrencyCode, { symbol: string; name: string; locale: string }> = {
  INR: { symbol: '\u20B9', name: 'Indian Rupee', locale: 'en-IN' },
  USD: { symbol: '$', name: 'US Dollar', locale: 'en-US' },
  EUR: { symbol: '\u20AC', name: 'Euro', locale: 'de-DE' },
  GBP: { symbol: '\u00A3', name: 'British Pound', locale: 'en-GB' },
  AED: { symbol: 'AED', name: 'UAE Dirham', locale: 'ar-AE' },
  SGD: { symbol: 'S$', name: 'Singapore Dollar', locale: 'en-SG' },
  THB: { symbol: '\u0E3F', name: 'Thai Baht', locale: 'th-TH' },
  JPY: { symbol: '\u00A5', name: 'Japanese Yen', locale: 'ja-JP' },
};

export const CURRENCY_OPTIONS: { value: CurrencyCode; label: string }[] = [
  { value: 'INR', label: 'INR (\u20B9)' },
  { value: 'USD', label: 'USD ($)' },
  { value: 'EUR', label: 'EUR (\u20AC)' },
  { value: 'GBP', label: 'GBP (\u00A3)' },
  { value: 'AED', label: 'AED' },
  { value: 'SGD', label: 'SGD (S$)' },
  { value: 'THB', label: 'THB (\u0E3F)' },
  { value: 'JPY', label: 'JPY (\u00A5)' },
];

export function formatCurrencyAmount(amount: number, currency: CurrencyCode = 'INR'): string {
  const info = CURRENCY_MAP[currency];
  if (!info) return `${amount}`;
  try {
    return new Intl.NumberFormat(info.locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: currency === 'JPY' ? 0 : 2,
      maximumFractionDigits: currency === 'JPY' ? 0 : 2,
    }).format(amount);
  } catch {
    return `${info.symbol}${amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
  }
}

export function getCurrencySymbol(currency: CurrencyCode = 'INR'): string {
  return CURRENCY_MAP[currency]?.symbol ?? currency;
}

// ---------------------------------------------------------------------------
// Locale-aware currency formatter
// ---------------------------------------------------------------------------
// Uses Intl.NumberFormat for proper locale-specific grouping, decimal, and
// currency symbol placement. Falls back to CURRENCY_MAP locale when the
// caller does not supply an explicit locale.
// ---------------------------------------------------------------------------
export function formatCurrency(
  amount: number,
  currency: CurrencyCode | string = 'INR',
  locale?: string
): string {
  const info = CURRENCY_MAP[currency as CurrencyCode];
  const resolvedLocale = locale ?? info?.locale ?? 'en-US';
  const isZeroDecimal = currency === 'JPY';

  try {
    return new Intl.NumberFormat(resolvedLocale, {
      style: 'currency',
      currency,
      minimumFractionDigits: isZeroDecimal ? 0 : 2,
      maximumFractionDigits: isZeroDecimal ? 0 : 2,
    }).format(amount);
  } catch {
    // Graceful fallback when the runtime does not support the currency/locale
    const symbol = info?.symbol ?? currency;
    const formatted = amount
      .toFixed(isZeroDecimal ? 0 : 2)
      .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return `${symbol}${formatted}`;
  }
}
